import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { ArrowLeft, Search, Loader2, CheckCircle, XCircle, Clock, Trash2, History, AlertTriangle, TrendingUp, TrendingDown, Minus, ShieldCheck, Eye, Mail, FileDown, GitCompareArrows } from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import ReactMarkdown from 'react-markdown';
import { WeakSentimentTrendComparison } from '@/components/WeakSentimentTrendComparison';
import { SentimentOutlookDiagram } from '@/components/SentimentOutlookDiagram';
import { PriceExpectationPanel } from '@/components/PriceExpectationPanel';

const MODELS = [
  { id: 'google/gemini-2.5-pro', label: 'Gemini 2.5 Pro', color: 'text-blue-600 dark:text-blue-400' },
  { id: 'google/gemini-2.5-flash', label: 'Gemini 2.5 Flash', color: 'text-cyan-600 dark:text-cyan-400' },
  { id: 'google/gemini-3-flash-preview', label: 'Gemini 3 Flash', color: 'text-teal-600 dark:text-teal-400' },
  { id: 'openai/gpt-5', label: 'GPT-5', color: 'text-emerald-600 dark:text-emerald-400' },
  { id: 'openai/gpt-5-mini', label: 'GPT-5 Mini', color: 'text-amber-600 dark:text-amber-400' },
  { id: 'openai/gpt-5.2', label: 'GPT-5.2', color: 'text-violet-600 dark:text-violet-400' },
];

type ModelStatus = 'idle' | 'loading' | 'success' | 'error';

interface ModelResult {
  modelId: string;
  modelLabel: string;
  status: ModelStatus;
  result?: any;
  error?: string;
  elapsedMs?: number;
}

interface SavedSearch {
  id: string;
  topic: string;
  results: any;
  models_used: number;
  created_at: string;
}

const getSentimentIcon = (sentiment: string) => {
  if (sentiment?.includes('bullish')) return <TrendingUp className="h-4 w-4 text-emerald-500" />;
  if (sentiment?.includes('bearish')) return <TrendingDown className="h-4 w-4 text-rose-500" />;
  return <Minus className="h-4 w-4 text-muted-foreground" />;
};

const getSentimentBadge = (sentiment: string) => {
  const s = sentiment?.toLowerCase() || '';
  if (s === 'very_bullish') return <Badge className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30">Very Bullish</Badge>;
  if (s === 'bullish') return <Badge className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20">Bullish</Badge>;
  if (s === 'very_bearish') return <Badge className="bg-rose-500/15 text-rose-700 dark:text-rose-400 border-rose-500/30">Very Bearish</Badge>;
  if (s === 'bearish') return <Badge className="bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/20">Bearish</Badge>;
  return <Badge variant="secondary">Neutral</Badge>;
};

const getStrengthBadge = (strength: string) => {
  if (strength === 'strengthening') return <Badge className="bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30 text-xs">Strengthening</Badge>;
  if (strength === 'developing') return <Badge className="bg-blue-500/15 text-blue-700 dark:text-blue-400 border-blue-500/30 text-xs">Developing</Badge>;
  return <Badge variant="outline" className="text-xs">Emerging</Badge>;
};

const getVerificationBadge = (status: string) => {
  if (status === 'verified') return <Badge className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30 text-xs"><ShieldCheck className="h-3 w-3 mr-1" />Verified</Badge>;
  if (status === 'partially_verified') return <Badge className="bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30 text-xs"><AlertTriangle className="h-3 w-3 mr-1" />Partial</Badge>;
  return <Badge className="bg-rose-500/15 text-rose-700 dark:text-rose-400 border-rose-500/30 text-xs"><XCircle className="h-3 w-3 mr-1" />Unverified</Badge>;
};

const WeakSentiments = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [topic, setTopic] = useState('');
  const [searching, setSearching] = useState(false);
  const [modelResults, setModelResults] = useState<ModelResult[]>([]);
  const [savedSearches, setSavedSearches] = useState<SavedSearch[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [expandedModel, setExpandedModel] = useState<string | null>(null);
  const [user, setUser] = useState<any>(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [showTrendComparison, setShowTrendComparison] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState(() => localStorage.getItem('weak_sentiment_recipient') || '');
  const [showRecipientInput, setShowRecipientInput] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (user) loadSavedSearches();
  }, [user]);

  const loadSavedSearches = async () => {
    setLoadingHistory(true);
    try {
      const { data, error } = await supabase
        .from('weak_sentiment_searches')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(50);
      if (error) throw error;
      setSavedSearches((data as any[]) || []);
    } catch (err) {
      console.error('Error loading saved searches:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const saveSearch = async (topic: string, results: ModelResult[]) => {
    if (!user) return;
    try {
      const successResults = results.filter(r => r.status === 'success');
      const { error } = await supabase.from('weak_sentiment_searches').insert({
        user_id: user.id,
        topic,
        results: successResults.map(r => ({
          modelId: r.modelId,
          modelLabel: r.modelLabel,
          result: r.result,
          elapsedMs: r.elapsedMs,
        })),
        models_used: successResults.length,
      } as any);
      if (error) throw error;
      loadSavedSearches();
      toast({ title: 'Search saved', description: `"${topic}" saved with ${successResults.length} model results.` });
    } catch (err) {
      console.error('Error saving search:', err);
    }
  };

  const deleteSearch = async (id: string) => {
    try {
      const { error } = await supabase.from('weak_sentiment_searches').delete().eq('id', id);
      if (error) throw error;
      setSavedSearches(prev => prev.filter(s => s.id !== id));
      toast({ title: 'Search deleted' });
    } catch (err) {
      console.error('Error deleting search:', err);
    }
  };

  const loadSearch = (search: SavedSearch) => {
    setTopic(search.topic);
    const results: ModelResult[] = (search.results as any[]).map((r: any) => ({
      modelId: r.modelId,
      modelLabel: r.modelLabel,
      status: 'success' as ModelStatus,
      result: r.result,
      elapsedMs: r.elapsedMs,
    }));
    setModelResults(results);
    setShowHistory(false);
    setExpandedModel(results[0]?.modelId || null);
  };

  const HORIZON_ORDER_EXPORT = ['1 Week', '1 Month', '3 Months', '6 Months', 'Other'];
  const normalizeHorizonExport = (h: string): string => {
    const lower = (h || '').toLowerCase();
    if (/immediate|day|week|1\s*w|short[\s-]*term|near[\s-]*term/.test(lower)) return '1 Week';
    if (/1[\s-]*(?:to[\s-]*)?3\s*month|q[1-4]|quarter|1m|2m|3m|medium[\s-]*term/.test(lower)) return '1 Month';
    if (/3[\s-]*(?:to[\s-]*)?6\s*month|4m|5m|6m|half[\s-]*year|mid[\s-]*term/.test(lower)) return '3 Months';
    if (/6[\s-]*(?:to[\s-]*)?12\s*month|year|annual|9m|12m|long[\s-]*term|multi[\s-]*year|secular|structural|2\+?\s*year/.test(lower)) return '6 Months';
    return 'Other';
  };

  const buildHorizonBreakdown = (rankedSignals: any[]) => {
    const grouped: Record<string, any[]> = {};
    rankedSignals.forEach(sig => {
      const bucket = normalizeHorizonExport(sig.timeHorizon);
      if (!grouped[bucket]) grouped[bucket] = [];
      grouped[bucket].push(sig);
    });
    return HORIZON_ORDER_EXPORT
      .filter(h => grouped[h]?.length > 0)
      .map(bucket => {
        const sigs = grouped[bucket];
        const bullish = sigs.filter((s: any) => s.direction === 'bullish').length;
        const bearish = sigs.filter((s: any) => s.direction === 'bearish').length;
        const neutral = sigs.filter((s: any) => s.direction === 'neutral').length;
        return { bucket, total: sigs.length, bullish, bearish, neutral, signals: sigs };
      });
  };

  const sendConsensusEmail = useCallback(async () => {
    const successResults = modelResults.filter(r => r.status === 'success' && r.result);
    if (successResults.length < 2) {
      toast({ title: 'Not enough data', description: 'Need at least 2 model results to send report.', variant: 'destructive' });
      return;
    }

    setSendingEmail(true);
    try {
      // Replicate the consensus computation for the email payload
      const sentimentVotes: Record<string, number> = {};
      successResults.forEach(r => {
        const s = r.result.overallSentiment || 'neutral';
        sentimentVotes[s] = (sentimentVotes[s] || 0) + 1;
      });
      const consensusSentiment = Object.entries(sentimentVotes).sort((a, b) => b[1] - a[1])[0];
      const agreementPct = Math.round((consensusSentiment[1] / successResults.length) * 100);
      const avgConfidence = Math.round(successResults.reduce((sum, r) => sum + (r.result.confidenceScore || 0), 0) / successResults.length);

      const signalMap: Record<string, any> = {};
      successResults.forEach(r => {
        (r.result.weakSignals || []).forEach((sig: any) => {
          const key = sig.signal?.toLowerCase().replace(/[^a-z0-9 ]/g, '').slice(0, 80) || '';
          if (!key) return;
          if (!signalMap[key]) {
            signalMap[key] = { ...sig, modelCount: 1, models: [r.modelLabel] };
          } else {
            signalMap[key].modelCount++;
            if (!signalMap[key].models.includes(r.modelLabel)) signalMap[key].models.push(r.modelLabel);
            if (sig.verificationStatus === 'verified') signalMap[key].verificationStatus = 'verified';
            else if (sig.verificationStatus === 'partially_verified' && signalMap[key].verificationStatus === 'unverified') {
              signalMap[key].verificationStatus = 'partially_verified';
            }
            const strengthRank: Record<string, number> = { emerging: 0, developing: 1, strengthening: 2 };
            if ((strengthRank[sig.strength] || 0) > (strengthRank[signalMap[key].strength] || 0)) {
              signalMap[key].strength = sig.strength;
            }
          }
        });
      });
      const rankedSignals = Object.values(signalMap).sort((a: any, b: any) => b.modelCount - a.modelCount).slice(0, 15);

      const insightMap: Record<string, { text: string; count: number }> = {};
      successResults.forEach(r => {
        (r.result.actionableInsights || []).forEach((ins: string) => {
          const key = ins.toLowerCase().replace(/[^a-z0-9 ]/g, '').slice(0, 60);
          if (!key) return;
          if (!insightMap[key]) insightMap[key] = { text: ins, count: 1 };
          else insightMap[key].count++;
        });
      });
      const topInsights = Object.values(insightMap).sort((a, b) => b.count - a.count).slice(0, 8);

      const riskMap: Record<string, { text: string; count: number }> = {};
      successResults.forEach(r => {
        (r.result.keyRisks || []).forEach((risk: string) => {
          const key = risk.toLowerCase().replace(/[^a-z0-9 ]/g, '').slice(0, 60);
          if (!key) return;
          if (!riskMap[key]) riskMap[key] = { text: risk, count: 1 };
          else riskMap[key].count++;
        });
      });
      const topRisks = Object.values(riskMap).sort((a, b) => b.count - a.count).slice(0, 8);

      const totalSources = successResults.reduce((s, r) => s + (r.result.dataQuality?.sourcesReferenced || 0), 0);
      const totalVerified = successResults.reduce((s, r) => s + (r.result.dataQuality?.verifiedClaims || 0), 0);
      const totalUnverified = successResults.reduce((s, r) => s + (r.result.dataQuality?.unverifiedClaims || 0), 0);

      const directionCounts = { bullish: 0, bearish: 0, neutral: 0 };
      rankedSignals.forEach((s: any) => {
        if (s.direction === 'bullish') directionCounts.bullish++;
        else if (s.direction === 'bearish') directionCounts.bearish++;
        else directionCounts.neutral++;
      });

      // Aggregate time horizon outlooks
      const PERIODS = ['1 Week', '1 Month', '3 Months', '6 Months'];
      const SENTIMENT_RANK: Record<string, number> = { very_bullish: 2, bullish: 1, neutral: 0, bearish: -1, very_bearish: -2 };
      const RANK_TO_SENTIMENT = ['very_bearish', 'bearish', 'neutral', 'bullish', 'very_bullish'];
      const timeHorizonOutlooks = PERIODS.map(period => {
        const entries: { sentiment: string; confidence: number; rationale: string }[] = [];
        successResults.forEach(r => {
          const outlooks = r.result.timeHorizonOutlook || [];
          const match = outlooks.find((o: any) => o.period === period);
          if (match) entries.push(match);
        });
        if (entries.length === 0) return { period, sentiment: 'neutral', confidence: 0, rationale: 'Insufficient data' };
        const avgRank = entries.reduce((sum, e) => sum + (SENTIMENT_RANK[e.sentiment] ?? 0), 0) / entries.length;
        const roundedIdx = Math.round(avgRank + 2);
        const consensusSent = RANK_TO_SENTIMENT[Math.max(0, Math.min(4, roundedIdx))];
        const avgConf = Math.round(entries.reduce((s, e) => s + (e.confidence || 50), 0) / entries.length);
        const best = entries.reduce((b, e) => Math.abs((SENTIMENT_RANK[e.sentiment] ?? 0) - avgRank) < Math.abs((SENTIMENT_RANK[b.sentiment] ?? 0) - avgRank) ? e : b, entries[0]);
        return { period, sentiment: consensusSent, confidence: avgConf, rationale: best.rationale };
      });
      const hasOutlookData = successResults.some(r => r.result.timeHorizonOutlook?.length > 0);

      // Build horizon-grouped signal breakdown
      const horizonBreakdown = buildHorizonBreakdown(rankedSignals);

      // Fetch latest price projection for this topic
      let priceProjections: any = undefined;
      try {
        const { data: projRows } = await supabase
          .from('price_projections')
          .select('*')
          .eq('topic', topic.trim())
          .order('created_at', { ascending: false })
          .limit(1);
        if (projRows?.[0]) {
          const p = projRows[0];
          priceProjections = {
            asset_type: p.asset_type,
            current_prices: p.current_prices,
            key_drivers: p.key_drivers,
            projections: p.projections,
            generated_at: p.created_at,
          };
        }
      } catch (err) {
        console.error('Error fetching projections for email:', err);
      }

      const { data, error } = await supabase.functions.invoke('send-weak-sentiment-report', {
        body: {
          topic: topic.trim(),
          recipientEmail: recipientEmail.trim() || undefined,
          consensusSentiment: consensusSentiment[0],
          agreementPct,
          avgConfidence,
          modelsUsed: successResults.length,
          rankedSignals,
          topInsights,
          topRisks,
          totalSources,
          totalVerified,
          totalUnverified,
          directionCounts,
          sentimentVotes,
          timeHorizonOutlooks: hasOutlookData ? timeHorizonOutlooks : undefined,
          horizonBreakdown: horizonBreakdown.length > 0 ? horizonBreakdown : undefined,
          priceProjections,
          modelResults: successResults.map(r => ({
            modelLabel: r.modelLabel,
            sentiment: r.result.overallSentiment || 'neutral',
            confidence: r.result.confidenceScore || 0,
            signalCount: r.result.weakSignals?.length || 0,
            elapsedMs: r.elapsedMs || 0,
            timeHorizonOutlook: r.result.timeHorizonOutlook || [],
          })),
        },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      toast({ title: 'Report sent!', description: `Consensus report emailed to ${data.recipient}` });
    } catch (err: any) {
      console.error('Error sending email report:', err);
      toast({ title: 'Email failed', description: err.message || 'Could not send report', variant: 'destructive' });
    } finally {
      setSendingEmail(false);
    }
  }, [modelResults, topic, toast, recipientEmail]);
  const exportConsensusPdf = useCallback(async () => {
    const successResults = modelResults.filter(r => r.status === 'success' && r.result);
    if (successResults.length < 2) {
      toast({ title: 'Not enough data', description: 'Need at least 2 model results to export PDF.', variant: 'destructive' });
      return;
    }

    const sentimentVotes: Record<string, number> = {};
    successResults.forEach(r => {
      const s = r.result.overallSentiment || 'neutral';
      sentimentVotes[s] = (sentimentVotes[s] || 0) + 1;
    });
    const consensusSentiment = Object.entries(sentimentVotes).sort((a, b) => b[1] - a[1])[0];
    const agreementPct = Math.round((consensusSentiment[1] / successResults.length) * 100);
    const avgConfidence = Math.round(successResults.reduce((sum, r) => sum + (r.result.confidenceScore || 0), 0) / successResults.length);

    const signalMap: Record<string, any> = {};
    successResults.forEach(r => {
      (r.result.weakSignals || []).forEach((sig: any) => {
        const key = sig.signal?.toLowerCase().replace(/[^a-z0-9 ]/g, '').slice(0, 80) || '';
        if (!key) return;
        if (!signalMap[key]) {
          signalMap[key] = { ...sig, modelCount: 1, models: [r.modelLabel] };
        } else {
          signalMap[key].modelCount++;
          if (!signalMap[key].models.includes(r.modelLabel)) signalMap[key].models.push(r.modelLabel);
          if (sig.verificationStatus === 'verified') signalMap[key].verificationStatus = 'verified';
          else if (sig.verificationStatus === 'partially_verified' && signalMap[key].verificationStatus === 'unverified') signalMap[key].verificationStatus = 'partially_verified';
          const sr: Record<string, number> = { emerging: 0, developing: 1, strengthening: 2 };
          if ((sr[sig.strength] || 0) > (sr[signalMap[key].strength] || 0)) signalMap[key].strength = sig.strength;
        }
      });
    });
    const rankedSignals = Object.values(signalMap).sort((a: any, b: any) => b.modelCount - a.modelCount).slice(0, 15);

    const insightMap: Record<string, { text: string; count: number }> = {};
    successResults.forEach(r => {
      (r.result.actionableInsights || []).forEach((ins: string) => {
        const k = ins.toLowerCase().replace(/[^a-z0-9 ]/g, '').slice(0, 60);
        if (!k) return;
        if (!insightMap[k]) insightMap[k] = { text: ins, count: 1 };
        else insightMap[k].count++;
      });
    });
    const topInsights = Object.values(insightMap).sort((a, b) => b.count - a.count).slice(0, 8);

    const riskMap: Record<string, { text: string; count: number }> = {};
    successResults.forEach(r => {
      (r.result.keyRisks || []).forEach((risk: string) => {
        const k = risk.toLowerCase().replace(/[^a-z0-9 ]/g, '').slice(0, 60);
        if (!k) return;
        if (!riskMap[k]) riskMap[k] = { text: risk, count: 1 };
        else riskMap[k].count++;
      });
    });
    const topRisks = Object.values(riskMap).sort((a, b) => b.count - a.count).slice(0, 8);

    const totalSources = successResults.reduce((s, r) => s + (r.result.dataQuality?.sourcesReferenced || 0), 0);
    const totalVerified = successResults.reduce((s, r) => s + (r.result.dataQuality?.verifiedClaims || 0), 0);
    const totalUnverified = successResults.reduce((s, r) => s + (r.result.dataQuality?.unverifiedClaims || 0), 0);
    const dirCounts = { bullish: 0, bearish: 0, neutral: 0 };
    rankedSignals.forEach((s: any) => {
      if (s.direction === 'bullish') dirCounts.bullish++;
      else if (s.direction === 'bearish') dirCounts.bearish++;
      else dirCounts.neutral++;
    });

    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pw = doc.internal.pageSize.getWidth();
    const margin = 14;
    let y = 16;

    // Title
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(124, 58, 237);
    doc.text('Weak Sentiment Consensus Report', pw / 2, y, { align: 'center' });
    y += 8;
    doc.setFontSize(12);
    doc.setTextColor(60, 60, 60);
    doc.text(`"${topic.trim()}"`, pw / 2, y, { align: 'center' });
    y += 6;
    doc.setFontSize(8);
    doc.setTextColor(140, 140, 140);
    doc.text(`Generated ${new Date().toLocaleString()} · ${successResults.length} AI Models · Triple-Loop Verified`, pw / 2, y, { align: 'center' });
    y += 10;

    // Summary banner
    doc.setFillColor(124, 58, 237);
    doc.roundedRect(margin, y, pw - 2 * margin, 18, 3, 3, 'F');
    doc.setFontSize(9);
    doc.setTextColor(255, 255, 255);
    const labels = [
      `Consensus: ${consensusSentiment[0].replace(/_/g, ' ').toUpperCase()}`,
      `Agreement: ${agreementPct}%`,
      `Avg Confidence: ${avgConfidence}%`,
      `Signals: ${rankedSignals.length}`,
      `Sources: ${totalSources}`,
      `Verified: ${totalVerified}`,
    ];
    const colW = (pw - 2 * margin) / labels.length;
    labels.forEach((lbl, i) => {
      doc.text(lbl, margin + colW * i + colW / 2, y + 10, { align: 'center' });
    });
    y += 24;

    // Direction bar
    const total = dirCounts.bullish + dirCounts.bearish + dirCounts.neutral || 1;
    const barW = pw - 2 * margin;
    const bullW = (dirCounts.bullish / total) * barW;
    const neutW = (dirCounts.neutral / total) * barW;
    const bearW = (dirCounts.bearish / total) * barW;
    doc.setFontSize(8);
    doc.setTextColor(80, 80, 80);
    doc.text('Signal Direction:', margin, y);
    y += 3;
    if (bullW > 0) { doc.setFillColor(16, 185, 129); doc.rect(margin, y, bullW, 4, 'F'); }
    if (neutW > 0) { doc.setFillColor(209, 213, 219); doc.rect(margin + bullW, y, neutW, 4, 'F'); }
    if (bearW > 0) { doc.setFillColor(239, 68, 68); doc.rect(margin + bullW + neutW, y, bearW, 4, 'F'); }
    y += 6;
    doc.setFontSize(7);
    doc.setTextColor(16, 185, 129); doc.text(`Bullish (${dirCounts.bullish})`, margin, y);
    doc.setTextColor(140, 140, 140); doc.text(`Neutral (${dirCounts.neutral})`, pw / 2, y, { align: 'center' });
    doc.setTextColor(239, 68, 68); doc.text(`Bearish (${dirCounts.bearish})`, pw - margin, y, { align: 'right' });
    y += 8;

    // Consensus Signals Table
    if (rankedSignals.length > 0) {
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(40, 40, 40);
      doc.text('Consensus Signal Ranking', margin, y);
      y += 4;

      autoTable(doc, {
        startY: y,
        margin: { left: margin, right: margin },
        head: [['#', 'Signal', 'Dir.', 'Strength', 'Verified', 'Models']],
        body: rankedSignals.map((sig: any, i: number) => [
          `#${i + 1}`,
          sig.signal?.substring(0, 80) || '',
          sig.direction,
          sig.strength,
          sig.verificationStatus === 'verified' ? 'Yes' : sig.verificationStatus === 'partially_verified' ? 'Partial' : 'No',
          `${sig.modelCount}/${successResults.length}`,
        ]),
        styles: { fontSize: 7, cellPadding: 2 },
        headStyles: { fillColor: [124, 58, 237], textColor: 255, fontStyle: 'bold' },
        columnStyles: {
          0: { cellWidth: 8 },
          1: { cellWidth: 'auto' },
          2: { cellWidth: 16 },
          3: { cellWidth: 22 },
          4: { cellWidth: 16 },
          5: { cellWidth: 16 },
        },
      });
      y = (doc as any).lastAutoTable.finalY + 6;
    }

    // Horizon-Grouped Signal Breakdown
    const pdfHorizonBreakdown = buildHorizonBreakdown(rankedSignals);
    if (pdfHorizonBreakdown.length > 0) {
      if (y > 230) { doc.addPage(); y = 16; }
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(40, 40, 40);
      doc.text('Signal Breakdown by Time Horizon', margin, y);
      y += 4;

      autoTable(doc, {
        startY: y,
        margin: { left: margin, right: margin },
        head: [['Horizon', 'Total', 'Bullish', 'Neutral', 'Bearish', 'Bias', 'Top Signals']],
        body: pdfHorizonBreakdown.map((hb: any) => {
          const score = hb.total > 0 ? (hb.bullish - hb.bearish) / hb.total : 0;
          const bias = score > 0.3 ? 'Bullish' : score < -0.3 ? 'Bearish' : 'Mixed';
          const topSigs = hb.signals.slice(0, 2).map((s: any) => s.signal?.substring(0, 40)).join('; ') || '-';
          return [hb.bucket, `${hb.total}`, `${hb.bullish}`, `${hb.neutral}`, `${hb.bearish}`, bias, topSigs];
        }),
        styles: { fontSize: 7, cellPadding: 2 },
        headStyles: { fillColor: [124, 58, 237], textColor: 255, fontStyle: 'bold' },
        columnStyles: {
          0: { cellWidth: 24 },
          1: { cellWidth: 12 },
          2: { cellWidth: 14 },
          3: { cellWidth: 14 },
          4: { cellWidth: 14 },
          5: { cellWidth: 16 },
          6: { cellWidth: 'auto' },
        },
        didParseCell: (data: any) => {
          if (data.section === 'body' && data.column.index === 5) {
            const val = (data.cell.raw as string || '').toLowerCase();
            if (val === 'bullish') data.cell.styles.textColor = [16, 185, 129];
            else if (val === 'bearish') data.cell.styles.textColor = [239, 68, 68];
            else data.cell.styles.textColor = [107, 114, 128];
            data.cell.styles.fontStyle = 'bold';
          }
        },
      });
      y = (doc as any).lastAutoTable.finalY + 6;
    }

    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(40, 40, 40);
    if (y > 250) { doc.addPage(); y = 16; }
    doc.text('Model Comparison', margin, y);
    y += 4;

    autoTable(doc, {
      startY: y,
      margin: { left: margin, right: margin },
      head: [['Model', 'Sentiment', 'Confidence', 'Signals', 'Time']],
      body: successResults.map(r => [
        r.modelLabel,
        (r.result.overallSentiment || 'neutral').replace(/_/g, ' '),
        `${r.result.confidenceScore || 0}%`,
        `${r.result.weakSignals?.length || 0}`,
        r.elapsedMs ? `${(r.elapsedMs / 1000).toFixed(1)}s` : '-',
      ]),
      styles: { fontSize: 8, cellPadding: 2.5 },
      headStyles: { fillColor: [124, 58, 237], textColor: 255, fontStyle: 'bold' },
    });
    y = (doc as any).lastAutoTable.finalY + 6;

    // Insights & Risks
    if (y > 250) { doc.addPage(); y = 16; }
    if (topInsights.length > 0) {
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(40, 40, 40);
      doc.text('Top Actionable Insights', margin, y);
      y += 5;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      topInsights.forEach(ins => {
        if (y > 275) { doc.addPage(); y = 16; }
        const lines = doc.splitTextToSize(`• ${ins.text}${ins.count > 1 ? ` (${ins.count}x)` : ''}`, pw - 2 * margin);
        doc.setTextColor(60, 60, 60);
        doc.text(lines, margin, y);
        y += lines.length * 4 + 1;
      });
      y += 4;
    }

    if (topRisks.length > 0) {
      if (y > 260) { doc.addPage(); y = 16; }
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(40, 40, 40);
      doc.text('Top Key Risks', margin, y);
      y += 5;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      topRisks.forEach(risk => {
        if (y > 275) { doc.addPage(); y = 16; }
        const lines = doc.splitTextToSize(`• ${risk.text}${risk.count > 1 ? ` (${risk.count}x)` : ''}`, pw - 2 * margin);
        doc.setTextColor(60, 60, 60);
        doc.text(lines, margin, y);
        y += lines.length * 4 + 1;
      });
    }

    // Time Horizon Outlook Table
    const pdfOutlookResults = successResults.filter(r => r.result.timeHorizonOutlook?.length > 0);
    if (pdfOutlookResults.length > 0) {
      const PERIODS_PDF = ['1 Week', '1 Month', '3 Months', '6 Months'];
      const SRANK: Record<string, number> = { very_bullish: 2, bullish: 1, neutral: 0, bearish: -1, very_bearish: -2 };
      const R2S = ['very_bearish', 'bearish', 'neutral', 'bullish', 'very_bullish'];

      const outlookRows = PERIODS_PDF.map(period => {
        const entries: { sentiment: string; confidence: number; rationale: string }[] = [];
        pdfOutlookResults.forEach(r => {
          const match = (r.result.timeHorizonOutlook || []).find((o: any) => o.period === period);
          if (match) entries.push(match);
        });
        if (entries.length === 0) return [period, 'N/A', '-', '-'];
        const avgRank = entries.reduce((s, e) => s + (SRANK[e.sentiment] ?? 0), 0) / entries.length;
        const idx = Math.max(0, Math.min(4, Math.round(avgRank + 2)));
        const sent = R2S[idx].replace(/_/g, ' ');
        const conf = Math.round(entries.reduce((s, e) => s + (e.confidence || 50), 0) / entries.length);
        const best = entries.reduce((b, e) => Math.abs((SRANK[e.sentiment] ?? 0) - avgRank) < Math.abs((SRANK[b.sentiment] ?? 0) - avgRank) ? e : b, entries[0]);
        return [period, sent, `${conf}%`, best.rationale?.substring(0, 90) || '-'];
      });

      if (y > 230) { doc.addPage(); y = 16; }
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(40, 40, 40);
      doc.text('Time Horizon Outlook', margin, y);
      y += 4;

      autoTable(doc, {
        startY: y,
        margin: { left: margin, right: margin },
        head: [['Period', 'Sentiment', 'Confidence', 'Rationale']],
        body: outlookRows,
        styles: { fontSize: 7, cellPadding: 2.5 },
        headStyles: { fillColor: [124, 58, 237], textColor: 255, fontStyle: 'bold' },
        columnStyles: {
          0: { cellWidth: 22 },
          1: { cellWidth: 24 },
          2: { cellWidth: 18 },
          3: { cellWidth: 'auto' },
        },
        didParseCell: (data: any) => {
          if (data.section === 'body' && data.column.index === 1) {
            const val = (data.cell.raw as string || '').toLowerCase();
            if (val.includes('bullish')) data.cell.styles.textColor = [16, 185, 129];
            else if (val.includes('bearish')) data.cell.styles.textColor = [239, 68, 68];
            else data.cell.styles.textColor = [107, 114, 128];
            data.cell.styles.fontStyle = 'bold';
          }
        },
      });
      y = (doc as any).lastAutoTable.finalY + 6;
    }

    // Per-Model Horizon Comparison Table
    if (pdfOutlookResults.length > 1) {
      const PERIODS_COMP = ['1 Week', '1 Month', '3 Months', '6 Months'];
      if (y > 200) { doc.addPage(); y = 16; }
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(40, 40, 40);
      doc.text('Per-Model Horizon Comparison', margin, y);
      y += 4;

      const modelHorizonHead = ['Model', ...PERIODS_COMP];
      const modelHorizonBody = pdfOutlookResults.map(r => {
        const row = [r.modelLabel];
        PERIODS_COMP.forEach(period => {
          const match = (r.result.timeHorizonOutlook || []).find((o: any) => o.period === period);
          if (match) {
            const label = (match.sentiment || 'neutral').replace(/_/g, ' ');
            row.push(`${label} (${match.confidence || 0}%)`);
          } else {
            row.push('-');
          }
        });
        return row;
      });

      autoTable(doc, {
        startY: y,
        margin: { left: margin, right: margin },
        head: [modelHorizonHead],
        body: modelHorizonBody,
        styles: { fontSize: 6.5, cellPadding: 2 },
        headStyles: { fillColor: [124, 58, 237], textColor: 255, fontStyle: 'bold', fontSize: 6.5 },
        columnStyles: {
          0: { cellWidth: 32, fontStyle: 'bold' },
        },
        didParseCell: (data: any) => {
          if (data.section === 'body' && data.column.index >= 1) {
            const val = (data.cell.raw as string || '').toLowerCase();
            if (val.includes('bullish')) data.cell.styles.textColor = [16, 185, 129];
            else if (val.includes('bearish')) data.cell.styles.textColor = [239, 68, 68];
            else if (val !== '-') data.cell.styles.textColor = [107, 114, 128];
          }
        },
      });
      y = (doc as any).lastAutoTable.finalY + 6;
    }

    // Price Projections Section
    try {
      const { data: projRows } = await supabase
        .from('price_projections')
        .select('*')
        .eq('topic', topic.trim())
        .order('created_at', { ascending: false })
        .limit(1);
      const proj = projRows?.[0];
      if (proj && proj.projections) {
        if (y > 200) { doc.addPage(); y = 16; }
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(40, 40, 40);
        doc.text('Price Projections (Consensus-Derived)', margin, y);
        y += 3;
        doc.setFontSize(7);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(140, 140, 140);
        doc.text(`Generated: ${new Date(proj.created_at).toLocaleString()} · Type: ${proj.asset_type || 'N/A'}`, margin, y);
        y += 3;
        if (Array.isArray(proj.key_drivers) && proj.key_drivers.length) {
          doc.text(`Key Drivers: ${(proj.key_drivers as string[]).join(' · ')}`, margin, y);
          y += 4;
        }

        const currencies = ['USD', 'EUR', 'CHF'];
        const horizons = ['1W', '1M', '3M', '6M'];
        const horizonLabels: Record<string, string> = { '1W': '1 Week', '1M': '1 Month', '3M': '3 Months', '6M': '6 Months' };
        const currSymbols: Record<string, string> = { USD: '$', EUR: '€', CHF: 'CHF ' };
        const fmtP = (v: number, c: string) => `${currSymbols[c] || ''}${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        const pctChg = (from: number, to: number) => {
          if (!from) return '—';
          const p = ((to - from) / from) * 100;
          return `${p >= 0 ? '+' : ''}${p.toFixed(1)}%`;
        };

        currencies.forEach(c => {
          if (y > 240) { doc.addPage(); y = 16; }
          const currentPrice = (proj.current_prices as any)?.[c] || 0;
          doc.setFontSize(9);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(124, 58, 237);
          doc.text(`${c} (Current: ${fmtP(currentPrice, c)})`, margin, y);
          y += 3;

          const projData = proj.projections as any;
          const tableBody = horizons.map(h => {
            const p = projData?.[h]?.[c];
            if (!p) return [horizonLabels[h], '-', '-', '-', '-', '-'];
            return [
              horizonLabels[h],
              fmtP(p.bear, c),
              fmtP(p.base, c),
              fmtP(p.bull, c),
              pctChg(currentPrice, p.base),
              (p.rationale || '-').substring(0, 70),
            ];
          });

          autoTable(doc, {
            startY: y,
            margin: { left: margin, right: margin },
            head: [['Horizon', 'Bear', 'Base', 'Bull', 'Δ Base', 'Rationale']],
            body: tableBody,
            styles: { fontSize: 7, cellPadding: 2 },
            headStyles: { fillColor: [124, 58, 237], textColor: 255, fontStyle: 'bold' },
            columnStyles: {
              0: { cellWidth: 20 },
              1: { cellWidth: 22 },
              2: { cellWidth: 22, fontStyle: 'bold' },
              3: { cellWidth: 22 },
              4: { cellWidth: 16 },
              5: { cellWidth: 'auto' },
            },
            didParseCell: (data: any) => {
              if (data.section === 'body') {
                if (data.column.index === 1) data.cell.styles.textColor = [239, 68, 68];
                if (data.column.index === 3) data.cell.styles.textColor = [16, 185, 129];
                if (data.column.index === 4) {
                  const val = data.cell.raw as string || '';
                  if (val.startsWith('+')) data.cell.styles.textColor = [16, 185, 129];
                  else if (val.startsWith('-')) data.cell.styles.textColor = [239, 68, 68];
                  data.cell.styles.fontStyle = 'bold';
                }
              }
            },
          });
          y = (doc as any).lastAutoTable.finalY + 5;
        });
      }
    } catch (err) {
      console.error('Error fetching projections for PDF:', err);
    }

    const pageCount = doc.getNumberOfPages();
    for (let p = 1; p <= pageCount; p++) {
      doc.setPage(p);
      doc.setFontSize(7);
      doc.setTextColor(160, 160, 160);
      doc.text(
        'Triple-Loop Verified · Anti-Hallucination Guardrails · For research purposes only — not financial advice.',
        pw / 2, doc.internal.pageSize.getHeight() - 6, { align: 'center' }
      );
      doc.text(`Page ${p}/${pageCount}`, pw - margin, doc.internal.pageSize.getHeight() - 6, { align: 'right' });
    }

    const filename = `weak-sentiment-${topic.trim().replace(/[^a-zA-Z0-9]/g, '-').slice(0, 40)}-${new Date().toISOString().slice(0, 10)}.pdf`;
    doc.save(filename);
    toast({ title: 'PDF exported', description: `Downloaded ${filename}` });
  }, [modelResults, topic, toast]);

  const runSearch = useCallback(async () => {
    if (!topic.trim()) return;

    setSearching(true);
    setExpandedModel(null);

    // Initialize all models as loading
    const initial: ModelResult[] = MODELS.map(m => ({
      modelId: m.id,
      modelLabel: m.label,
      status: 'loading',
    }));
    setModelResults(initial);

    const finalResults: ModelResult[] = [...initial];

    // Fire all 6 models in parallel
    const promises = MODELS.map(async (model, idx) => {
      try {
        const { data, error } = await supabase.functions.invoke('weak-sentiment-search', {
          body: { topic: topic.trim(), model: model.id },
        });

        if (error) throw error;
        if (data?.error) throw new Error(data.error);

        finalResults[idx] = {
          modelId: model.id,
          modelLabel: model.label,
          status: 'success',
          result: data.result,
          elapsedMs: data.elapsedMs,
        };
      } catch (err: any) {
        finalResults[idx] = {
          modelId: model.id,
          modelLabel: model.label,
          status: 'error',
          error: err.message || 'Unknown error',
        };
      }

      // Update state progressively
      setModelResults([...finalResults]);
    });

    await Promise.all(promises);
    setSearching(false);

    // Auto-expand first successful result
    const firstSuccess = finalResults.find(r => r.status === 'success');
    if (firstSuccess) setExpandedModel(firstSuccess.modelId);

    // Auto-save
    await saveSearch(topic.trim(), finalResults);
  }, [topic, user]);

  const completedCount = modelResults.filter(r => r.status === 'success' || r.status === 'error').length;
  const successCount = modelResults.filter(r => r.status === 'success').length;
  const progressPct = modelResults.length > 0 ? (completedCount / modelResults.length) * 100 : 0;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b bg-card/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-violet-600 dark:text-violet-400" />
              <h1 className="text-lg font-bold text-foreground">Weak Sentiments</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant={showTrendComparison ? 'default' : 'outline'}
              size="sm"
              onClick={() => { setShowTrendComparison(!showTrendComparison); if (!showTrendComparison) setShowHistory(false); }}
              className="gap-1.5"
              disabled={savedSearches.length < 2}
            >
              <GitCompareArrows className="h-4 w-4" />
              Trends
            </Button>
            <Button
              variant={showHistory ? 'default' : 'outline'}
              size="sm"
              onClick={() => { setShowHistory(!showHistory); if (!showHistory) setShowTrendComparison(false); }}
              className="gap-1.5"
            >
              <History className="h-4 w-4" />
              History
              {savedSearches.length > 0 && (
                <Badge variant="secondary" className="text-xs ml-1">{savedSearches.length}</Badge>
              )}
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Search Bar */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Search className="h-4 w-4 text-violet-600 dark:text-violet-400" />
              Research Topic
            </CardTitle>
            <CardDescription>
              Enter a thematic topic to scan for weak sentiment signals across 6 AI models with triple-loop verification guardrails
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-3">
              <Input
                placeholder="e.g. 'Semiconductor supply chain disruptions in Q2 2026' or 'European energy policy shifts'..."
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && !searching && runSearch()}
                className="flex-1"
                disabled={searching}
              />
              <Button
                onClick={runSearch}
                disabled={!topic.trim() || searching}
                className="gap-2 min-w-[140px]"
              >
                {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                {searching ? 'Researching...' : 'Search'}
              </Button>
            </div>

            {/* Guardrails Badge */}
            <div className="flex items-center gap-2 mt-3 flex-wrap">
              <Badge variant="outline" className="text-xs gap-1 border-emerald-500/30 text-emerald-700 dark:text-emerald-400">
                <ShieldCheck className="h-3 w-3" /> No Hallucinations
              </Badge>
              <Badge variant="outline" className="text-xs gap-1 border-amber-500/30 text-amber-700 dark:text-amber-400">
                <AlertTriangle className="h-3 w-3" /> No Guessing
              </Badge>
              <Badge variant="outline" className="text-xs gap-1 border-blue-500/30 text-blue-700 dark:text-blue-400">
                <CheckCircle className="h-3 w-3" /> Triple-Loop Verification
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Progress Bar */}
        {modelResults.length > 0 && (
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-foreground">
                  {searching ? 'Scanning models...' : `Complete — ${successCount}/${MODELS.length} models succeeded`}
                </span>
                <span className="text-xs text-muted-foreground">{completedCount}/{MODELS.length}</span>
              </div>
              <Progress value={progressPct} className="h-2" />

              {/* Model Status Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mt-3">
                {modelResults.map((mr) => {
                  const modelDef = MODELS.find(m => m.id === mr.modelId);
                  return (
                    <button
                      key={mr.modelId}
                      onClick={() => mr.status === 'success' && setExpandedModel(expandedModel === mr.modelId ? null : mr.modelId)}
                      className={`flex items-center gap-2 p-2 rounded-lg border transition-all text-left ${
                        expandedModel === mr.modelId
                          ? 'border-primary bg-primary/5'
                          : mr.status === 'success'
                            ? 'border-emerald-500/30 bg-emerald-500/5 hover:bg-emerald-500/10 cursor-pointer'
                            : mr.status === 'error'
                              ? 'border-rose-500/30 bg-rose-500/5'
                              : 'border-border bg-muted/30'
                      }`}
                    >
                      {mr.status === 'loading' && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground shrink-0" />}
                      {mr.status === 'success' && <CheckCircle className="h-3.5 w-3.5 text-emerald-500 shrink-0" />}
                      {mr.status === 'error' && <XCircle className="h-3.5 w-3.5 text-rose-500 shrink-0" />}
                      {mr.status === 'idle' && <Clock className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
                      <div className="min-w-0">
                        <p className={`text-xs font-medium truncate ${modelDef?.color || 'text-foreground'}`}>{mr.modelLabel}</p>
                        {mr.elapsedMs && <p className="text-[10px] text-muted-foreground">{(mr.elapsedMs / 1000).toFixed(1)}s</p>}
                        {mr.status === 'success' && mr.result?.confidenceScore !== undefined && (
                          <p className="text-[10px] text-muted-foreground">Quality: {mr.result.confidenceScore}%</p>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Cross-Model Consensus Summary */}
        {(() => {
          const successResults = modelResults.filter(r => r.status === 'success' && r.result);
          if (successResults.length < 2) return null;

          // Aggregate sentiment votes
          const sentimentVotes: Record<string, number> = {};
          successResults.forEach(r => {
            const s = r.result.overallSentiment || 'neutral';
            sentimentVotes[s] = (sentimentVotes[s] || 0) + 1;
          });
          const consensusSentiment = Object.entries(sentimentVotes).sort((a, b) => b[1] - a[1])[0];
          const agreementPct = Math.round((consensusSentiment[1] / successResults.length) * 100);

          // Average confidence
          const avgConfidence = Math.round(
            successResults.reduce((sum, r) => sum + (r.result.confidenceScore || 0), 0) / successResults.length
          );

          // Aggregate all weak signals and rank by cross-model frequency
          const signalMap: Record<string, { signal: string; direction: string; strength: string; source: string; timeHorizon: string; verificationStatus: string; modelCount: number; models: string[] }> = {};
          successResults.forEach(r => {
            (r.result.weakSignals || []).forEach((sig: any) => {
              // Normalize: use first 80 chars as a rough dedup key
              const key = sig.signal?.toLowerCase().replace(/[^a-z0-9 ]/g, '').slice(0, 80) || '';
              if (!key) return;
              if (!signalMap[key]) {
                signalMap[key] = { ...sig, modelCount: 1, models: [r.modelLabel] };
              } else {
                signalMap[key].modelCount++;
                if (!signalMap[key].models.includes(r.modelLabel)) {
                  signalMap[key].models.push(r.modelLabel);
                }
                // Upgrade verification if any model verified it
                if (sig.verificationStatus === 'verified') signalMap[key].verificationStatus = 'verified';
                else if (sig.verificationStatus === 'partially_verified' && signalMap[key].verificationStatus === 'unverified') {
                  signalMap[key].verificationStatus = 'partially_verified';
                }
                // Upgrade strength
                const strengthRank = { emerging: 0, developing: 1, strengthening: 2 } as Record<string, number>;
                if ((strengthRank[sig.strength] || 0) > (strengthRank[signalMap[key].strength] || 0)) {
                  signalMap[key].strength = sig.strength;
                }
              }
            });
          });
          const rankedSignals = Object.values(signalMap).sort((a, b) => b.modelCount - a.modelCount).slice(0, 15);

          // Aggregate actionable insights by frequency
          const insightMap: Record<string, { text: string; count: number }> = {};
          successResults.forEach(r => {
            (r.result.actionableInsights || []).forEach((ins: string) => {
              const key = ins.toLowerCase().replace(/[^a-z0-9 ]/g, '').slice(0, 60);
              if (!key) return;
              if (!insightMap[key]) insightMap[key] = { text: ins, count: 1 };
              else insightMap[key].count++;
            });
          });
          const topInsights = Object.values(insightMap).sort((a, b) => b.count - a.count).slice(0, 8);

          // Aggregate key risks
          const riskMap: Record<string, { text: string; count: number }> = {};
          successResults.forEach(r => {
            (r.result.keyRisks || []).forEach((risk: string) => {
              const key = risk.toLowerCase().replace(/[^a-z0-9 ]/g, '').slice(0, 60);
              if (!key) return;
              if (!riskMap[key]) riskMap[key] = { text: risk, count: 1 };
              else riskMap[key].count++;
            });
          });
          const topRisks = Object.values(riskMap).sort((a, b) => b.count - a.count).slice(0, 8);

          // Data quality aggregation
          const totalSources = successResults.reduce((s, r) => s + (r.result.dataQuality?.sourcesReferenced || 0), 0);
          const totalVerified = successResults.reduce((s, r) => s + (r.result.dataQuality?.verifiedClaims || 0), 0);
          const totalUnverified = successResults.reduce((s, r) => s + (r.result.dataQuality?.unverifiedClaims || 0), 0);

          // Direction breakdown
          const directionCounts = { bullish: 0, bearish: 0, neutral: 0 };
          rankedSignals.forEach(s => {
            if (s.direction === 'bullish') directionCounts.bullish++;
            else if (s.direction === 'bearish') directionCounts.bearish++;
            else directionCounts.neutral++;
          });

          return (
            <Card className="border-violet-500/30 bg-gradient-to-br from-violet-500/5 to-transparent">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-full bg-violet-500/15 flex items-center justify-center">
                      <ShieldCheck className="h-4 w-4 text-violet-600 dark:text-violet-400" />
                    </div>
                    <div>
                      <CardTitle className="text-base">Cross-Model Consensus</CardTitle>
                      <CardDescription>{successResults.length} models aggregated</CardDescription>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {getSentimentBadge(consensusSentiment[0])}
                    <Badge variant="outline" className="text-xs">{agreementPct}% agreement</Badge>
                    <div className="flex items-center gap-1.5">
                      {showRecipientInput && (
                        <Input
                          type="email"
                          placeholder="Recipient email (leave empty for default)"
                          value={recipientEmail}
                          onChange={(e) => {
                            setRecipientEmail(e.target.value);
                            localStorage.setItem('weak_sentiment_recipient', e.target.value);
                          }}
                          className="h-8 w-56 text-xs"
                        />
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => setShowRecipientInput(!showRecipientInput)}
                        title="Set recipient email"
                      >
                        <Mail className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-1.5"
                        onClick={sendConsensusEmail}
                        disabled={sendingEmail}
                      >
                        {sendingEmail ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />}
                        {sendingEmail ? 'Sending...' : recipientEmail.trim() ? `Email to ${recipientEmail.trim().split('@')[0]}` : 'Email Report'}
                      </Button>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1.5"
                      onClick={exportConsensusPdf}
                    >
                      <FileDown className="h-3.5 w-3.5" />
                      PDF Export
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-5">
                {/* Consensus Stats */}
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                  <div className="rounded-lg border bg-card p-3 text-center">
                    <p className="text-xs text-muted-foreground">Avg Confidence</p>
                    <p className="text-lg font-bold text-foreground">{avgConfidence}%</p>
                  </div>
                  <div className="rounded-lg border bg-card p-3 text-center">
                    <p className="text-xs text-muted-foreground">Unique Signals</p>
                    <p className="text-lg font-bold text-foreground">{rankedSignals.length}</p>
                  </div>
                  <div className="rounded-lg border bg-card p-3 text-center">
                    <p className="text-xs text-muted-foreground">Total Sources</p>
                    <p className="text-lg font-bold text-foreground">{totalSources}</p>
                  </div>
                  <div className="rounded-lg border bg-emerald-500/5 p-3 text-center">
                    <p className="text-xs text-muted-foreground">Verified</p>
                    <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400">{totalVerified}</p>
                  </div>
                  <div className="rounded-lg border bg-rose-500/5 p-3 text-center">
                    <p className="text-xs text-muted-foreground">Unverified</p>
                    <p className="text-lg font-bold text-rose-600 dark:text-rose-400">{totalUnverified}</p>
                  </div>
                </div>

                {/* Sentiment Heatmap */}
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-medium text-muted-foreground">Sentiment votes:</span>
                  {Object.entries(sentimentVotes).sort((a, b) => b[1] - a[1]).map(([s, count]) => (
                    <Badge key={s} variant="outline" className="text-xs gap-1">
                      {getSentimentIcon(s)} {s.replace('_', ' ')} × {count}
                    </Badge>
                  ))}
                </div>

                {/* Signal Direction Bar */}
                {rankedSignals.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-1.5">Signal Direction Breakdown</p>
                    <div className="flex h-3 rounded-full overflow-hidden">
                      {directionCounts.bullish > 0 && (
                        <div className="bg-emerald-500" style={{ width: `${(directionCounts.bullish / rankedSignals.length) * 100}%` }} />
                      )}
                      {directionCounts.neutral > 0 && (
                        <div className="bg-muted-foreground/30" style={{ width: `${(directionCounts.neutral / rankedSignals.length) * 100}%` }} />
                      )}
                      {directionCounts.bearish > 0 && (
                        <div className="bg-rose-500" style={{ width: `${(directionCounts.bearish / rankedSignals.length) * 100}%` }} />
                      )}
                    </div>
                    <div className="flex justify-between mt-1">
                      <span className="text-[10px] text-emerald-600 dark:text-emerald-400">Bullish ({directionCounts.bullish})</span>
                      <span className="text-[10px] text-muted-foreground">Neutral ({directionCounts.neutral})</span>
                      <span className="text-[10px] text-rose-600 dark:text-rose-400">Bearish ({directionCounts.bearish})</span>
                    </div>
                  </div>
                )}

                {/* Signals by Time Horizon with Bullish/Bearish Diagram */}
                {rankedSignals.length > 0 && (() => {
                  // Group signals by time horizon
                   const HORIZON_ORDER = ['1 Week', '1 Month', '3 Months', '6 Months', 'Other'];
                  const normalizeHorizon = (h: string): string => {
                    const lower = (h || '').toLowerCase();
                    if (/immediate|day|week|1\s*w|short[\s-]*term|near[\s-]*term/.test(lower)) return '1 Week';
                    if (/1[\s-]*(?:to[\s-]*)?3\s*month|q[1-4]|quarter|1m|2m|3m|medium[\s-]*term/.test(lower)) return '1 Month';
                    if (/3[\s-]*(?:to[\s-]*)?6\s*month|4m|5m|6m|half[\s-]*year|mid[\s-]*term/.test(lower)) return '3 Months';
                    if (/6[\s-]*(?:to[\s-]*)?12\s*month|year|annual|9m|12m|long[\s-]*term|multi[\s-]*year|secular|structural|2\+?\s*year/.test(lower)) return '6 Months';
                    return 'Other';
                  };

                  const grouped: Record<string, typeof rankedSignals> = {};
                  rankedSignals.forEach(sig => {
                    const bucket = normalizeHorizon(sig.timeHorizon);
                    if (!grouped[bucket]) grouped[bucket] = [];
                    grouped[bucket].push(sig);
                  });

                  const orderedBuckets = HORIZON_ORDER.filter(h => grouped[h]?.length > 0);

                  // Compute bullish/bearish/neutral counts per bucket for the diagram
                  const bucketStats = orderedBuckets.map(bucket => {
                    const sigs = grouped[bucket];
                    const bullish = sigs.filter(s => s.direction === 'bullish').length;
                    const bearish = sigs.filter(s => s.direction === 'bearish').length;
                    const neutral = sigs.filter(s => s.direction === 'neutral').length;
                    const total = sigs.length;
                    // Score: -1 (full bearish) to +1 (full bullish)
                    const score = total > 0 ? (bullish - bearish) / total : 0;
                    return { bucket, bullish, bearish, neutral, total, score };
                  });

                  return (
                    <div className="space-y-4">
                      <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
                        <Eye className="h-4 w-4 text-violet-500" />
                        Signals by Time Horizon
                      </h4>

                      {/* Progressive Bullish/Bearish Horizon Diagram */}
                      <div className="rounded-lg border bg-card p-4 space-y-3">
                        <p className="text-xs font-medium text-muted-foreground mb-2">Directional Bias by Horizon</p>
                        {bucketStats.map((bs, i) => {
                          const bullPct = bs.total > 0 ? (bs.bullish / bs.total) * 100 : 0;
                          const neutralPct = bs.total > 0 ? (bs.neutral / bs.total) * 100 : 0;
                          const bearPct = bs.total > 0 ? (bs.bearish / bs.total) * 100 : 0;
                          // Map score (-1..+1) to a position on a 0..100 scale, 50 = neutral
                          const needlePos = 50 + bs.score * 50;

                          return (
                            <div key={i} className="space-y-1">
                              <div className="flex items-center justify-between">
                                <span className="text-xs font-semibold text-foreground">{bs.bucket}</span>
                                <div className="flex items-center gap-2 text-[10px]">
                                  <span className="text-emerald-600 dark:text-emerald-400">▲ {bs.bullish}</span>
                                  <span className="text-muted-foreground">● {bs.neutral}</span>
                                  <span className="text-rose-600 dark:text-rose-400">▼ {bs.bearish}</span>
                                </div>
                              </div>

                              {/* Stacked direction bar */}
                              <div className="flex h-4 rounded-full overflow-hidden bg-muted/30">
                                {bullPct > 0 && (
                                  <div
                                    className="bg-emerald-500 flex items-center justify-center"
                                    style={{ width: `${bullPct}%` }}
                                  >
                                    {bullPct > 15 && <span className="text-[9px] text-white font-medium">{Math.round(bullPct)}%</span>}
                                  </div>
                                )}
                                {neutralPct > 0 && (
                                  <div
                                    className="bg-muted-foreground/25 flex items-center justify-center"
                                    style={{ width: `${neutralPct}%` }}
                                  >
                                    {neutralPct > 15 && <span className="text-[9px] text-foreground/70 font-medium">{Math.round(neutralPct)}%</span>}
                                  </div>
                                )}
                                {bearPct > 0 && (
                                  <div
                                    className="bg-rose-500 flex items-center justify-center"
                                    style={{ width: `${bearPct}%` }}
                                  >
                                    {bearPct > 15 && <span className="text-[9px] text-white font-medium">{Math.round(bearPct)}%</span>}
                                  </div>
                                )}
                              </div>

                              {/* Needle gauge */}
                              <div className="relative h-2 mt-0.5">
                                <div className="absolute inset-0 rounded-full overflow-hidden flex">
                                  <div className="flex-1 bg-gradient-to-r from-rose-500 via-rose-300 to-transparent" />
                                  <div className="w-px bg-border" />
                                  <div className="flex-1 bg-gradient-to-l from-emerald-500 via-emerald-300 to-transparent" />
                                </div>
                                {/* Needle marker */}
                                <div
                                  className="absolute top-[-2px] w-1.5 h-3 rounded-full bg-foreground shadow-sm transition-all duration-500"
                                  style={{ left: `calc(${needlePos}% - 3px)` }}
                                />
                              </div>
                            </div>
                          );
                        })}
                        <div className="flex justify-between text-[9px] text-muted-foreground pt-1 border-t border-border/50 mt-2">
                          <span className="text-rose-500">← Bearish</span>
                          <span>Neutral</span>
                          <span className="text-emerald-500">Bullish →</span>
                        </div>
                      </div>

                      {/* Signal Cards grouped by horizon */}
                      {orderedBuckets.map(bucket => (
                        <div key={bucket}>
                          <div className="flex items-center gap-2 mb-2">
                            <Badge variant="outline" className="text-xs font-semibold">{bucket}</Badge>
                            <span className="text-[10px] text-muted-foreground">{grouped[bucket].length} signal{grouped[bucket].length > 1 ? 's' : ''}</span>
                          </div>
                          <div className="space-y-2">
                            {grouped[bucket]
                              .sort((a, b) => b.modelCount - a.modelCount)
                              .map((sig, i) => (
                                <div key={i} className="rounded-lg border p-3 hover:bg-muted/30 transition-colors">
                                  <div className="flex items-start justify-between gap-3">
                                    <div className="flex items-start gap-2.5 flex-1">
                                      <div className="flex-1">
                                        <p className="text-sm font-medium text-foreground">{sig.signal}</p>
                                        <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                                          <Badge variant={sig.direction === 'bullish' ? 'default' : sig.direction === 'bearish' ? 'destructive' : 'secondary'} className="text-xs">
                                            {sig.direction}
                                          </Badge>
                                          {getStrengthBadge(sig.strength)}
                                          {getVerificationBadge(sig.verificationStatus)}
                                          <span className="text-xs text-muted-foreground">{sig.timeHorizon}</span>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="shrink-0 text-right">
                                      <Badge className="bg-violet-500/15 text-violet-700 dark:text-violet-400 border-violet-500/30 text-xs">
                                        {sig.modelCount}/{successResults.length} models
                                      </Badge>
                                      <p className="text-[10px] text-muted-foreground mt-1">{sig.models.join(', ')}</p>
                                    </div>
                                  </div>
                                </div>
                              ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}

                {/* Consensus Insights & Risks */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {topInsights.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-foreground mb-2">Top Actionable Insights</h4>
                      <ul className="space-y-1.5">
                        {topInsights.map((ins, i) => (
                          <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                            <span className="text-emerald-500 mt-0.5 shrink-0">✓</span>
                            <span>{ins.text}</span>
                            {ins.count > 1 && <Badge variant="outline" className="text-[10px] shrink-0 ml-auto">{ins.count}×</Badge>}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {topRisks.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-foreground mb-2">Top Key Risks</h4>
                      <ul className="space-y-1.5">
                        {topRisks.map((risk, i) => (
                          <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                            <span className="text-rose-500 mt-0.5 shrink-0">•</span>
                            <span>{risk.text}</span>
                            {risk.count > 1 && <Badge variant="outline" className="text-[10px] shrink-0 ml-auto">{risk.count}×</Badge>}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })()}

        {/* Sentiment Outlook Timeline Diagram */}
        {(() => {
          const successResults = modelResults.filter(r => r.status === 'success' && r.result);
          if (successResults.length < 2) return null;

          // Aggregate time horizon outlooks across models
           const PERIODS = ['1 Week', '1 Month', '3 Months', '6 Months'];
          const SENTIMENT_RANK: Record<string, number> = {
            very_bullish: 2, bullish: 1, neutral: 0, bearish: -1, very_bearish: -2,
          };
          const RANK_TO_SENTIMENT = ['very_bearish', 'bearish', 'neutral', 'bullish', 'very_bullish'];

          const aggregated = PERIODS.map(period => {
            const entries: { sentiment: string; confidence: number; rationale: string }[] = [];
            successResults.forEach(r => {
              const outlooks = r.result.timeHorizonOutlook || [];
              const match = outlooks.find((o: any) => o.period === period);
              if (match) entries.push(match);
            });

            if (entries.length === 0) {
              return { period, sentiment: 'neutral' as const, confidence: 0, rationale: 'Insufficient data from models' };
            }

            // Weighted average of sentiment positions
            const avgRank = entries.reduce((sum, e) => sum + (SENTIMENT_RANK[e.sentiment] ?? 0), 0) / entries.length;
            const roundedIdx = Math.round(avgRank + 2); // shift -2..2 to 0..4
            const consensusSentiment = RANK_TO_SENTIMENT[Math.max(0, Math.min(4, roundedIdx))] as any;
            const avgConfidence = Math.round(entries.reduce((s, e) => s + (e.confidence || 50), 0) / entries.length);
            // Pick rationale from model closest to consensus
            const best = entries.reduce((best, e) => {
              const dist = Math.abs((SENTIMENT_RANK[e.sentiment] ?? 0) - avgRank);
              const bestDist = Math.abs((SENTIMENT_RANK[best.sentiment] ?? 0) - avgRank);
              return dist < bestDist ? e : best;
            }, entries[0]);

            return { period, sentiment: consensusSentiment, confidence: avgConfidence, rationale: best.rationale };
          });

          // Only show if at least some models returned outlook data
          const hasData = successResults.some(r => r.result.timeHorizonOutlook?.length > 0);
          if (!hasData) return null;

          return (
            <SentimentOutlookDiagram
              outlooks={aggregated}
              topic={topic}
              modelsCount={successResults.length}
            />
          );
        })()}

        {/* Per-Model Time Horizon Comparison */}
        {(() => {
          const successResults = modelResults.filter(r => r.status === 'success' && r.result);
          const modelsWithOutlook = successResults.filter(r => r.result.timeHorizonOutlook?.length > 0);
          if (modelsWithOutlook.length < 2) return null;

          const PERIODS = ['1 Week', '1 Month', '3 Months', '6 Months'];
          const SENTIMENT_RANK: Record<string, number> = { very_bullish: 2, bullish: 1, neutral: 0, bearish: -1, very_bearish: -2 };

          const getSentimentStyle = (s: string) => {
            if (s.includes('very_bullish')) return 'bg-emerald-600 text-white';
            if (s.includes('bullish')) return 'bg-emerald-400/80 text-emerald-950';
            if (s.includes('very_bearish')) return 'bg-rose-600 text-white';
            if (s.includes('bearish')) return 'bg-rose-400/80 text-rose-950';
            return 'bg-muted text-muted-foreground';
          };

          // Calculate divergence per period
          const periodDivergence = PERIODS.map(period => {
            const ranks = modelsWithOutlook.map(r => {
              const match = (r.result.timeHorizonOutlook || []).find((o: any) => o.period === period);
              return match ? (SENTIMENT_RANK[match.sentiment] ?? 0) : 0;
            });
            const max = Math.max(...ranks);
            const min = Math.min(...ranks);
            return max - min; // 0 = consensus, 4 = max divergence
          });

          return (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  📊 Per-Model Time Horizon Comparison
                </CardTitle>
                <CardDescription>
                  How each LLM projects sentiment across time horizons — highlighting disagreements
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left p-2 font-semibold text-muted-foreground sticky left-0 bg-card z-10 min-w-[120px]">Model</th>
                        {PERIODS.map((p, i) => (
                          <th key={p} className="text-center p-2 font-semibold text-muted-foreground min-w-[100px]">
                            <div>{p}</div>
                            {periodDivergence[i] >= 2 && (
                              <div className="text-[10px] text-rose-500 font-normal mt-0.5">⚡ High divergence</div>
                            )}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {modelsWithOutlook.map(r => {
                        const outlooks = r.result.timeHorizonOutlook || [];
                        return (
                          <tr key={r.modelId} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                            <td className="p-2 font-medium text-foreground sticky left-0 bg-card z-10 whitespace-nowrap text-xs">
                              {r.modelLabel}
                            </td>
                            {PERIODS.map(period => {
                              const match = outlooks.find((o: any) => o.period === period);
                              if (!match) return (
                                <td key={period} className="p-2 text-center">
                                  <span className="text-xs text-muted-foreground">—</span>
                                </td>
                              );
                              const label = match.sentiment.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase());
                              return (
                                <td key={period} className="p-2 text-center">
                                  <div className="flex flex-col items-center gap-1">
                                    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${getSentimentStyle(match.sentiment)}`}>
                                      {label}
                                    </span>
                                    <span className="text-[10px] text-muted-foreground">{match.confidence}%</span>
                                  </div>
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                      {/* Divergence row */}
                      <tr className="border-t-2 border-border bg-muted/20">
                        <td className="p-2 font-semibold text-xs text-muted-foreground sticky left-0 bg-muted/20 z-10">Spread</td>
                        {PERIODS.map((period, i) => {
                          const div = periodDivergence[i];
                          const color = div === 0 ? 'text-emerald-600' : div <= 1 ? 'text-amber-600' : 'text-rose-600';
                          const label = div === 0 ? 'Consensus' : div <= 1 ? 'Minor' : div <= 2 ? 'Moderate' : 'High';
                          return (
                            <td key={period} className="p-2 text-center">
                              <span className={`text-xs font-semibold ${color}`}>{label}</span>
                              <div className="w-full h-1.5 rounded-full bg-muted mt-1 overflow-hidden">
                                <div 
                                  className={`h-full rounded-full ${div === 0 ? 'bg-emerald-500' : div <= 1 ? 'bg-amber-500' : 'bg-rose-500'}`}
                                  style={{ width: `${Math.min(100, (div / 4) * 100)}%` }}
                                />
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          );
        })()}

        {expandedModel && (() => {
          const mr = modelResults.find(r => r.modelId === expandedModel);
          if (!mr || mr.status !== 'success' || !mr.result) return null;
          const res = mr.result;
          return (
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <CardTitle className="text-base">{mr.modelLabel} Analysis</CardTitle>
                    {getSentimentBadge(res.overallSentiment)}
                    {getSentimentIcon(res.overallSentiment)}
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground">Confidence</p>
                      <p className="text-lg font-bold text-foreground">{res.confidenceScore}%</p>
                    </div>
                    {mr.elapsedMs && (
                      <div className="text-right">
                        <p className="text-xs text-muted-foreground">Time</p>
                        <p className="text-sm font-medium text-muted-foreground">{(mr.elapsedMs / 1000).toFixed(1)}s</p>
                      </div>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-5">
                {/* Data Quality Card */}
                {res.dataQuality && (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="rounded-lg border bg-muted/30 p-3 text-center">
                      <p className="text-xs text-muted-foreground">Sources</p>
                      <p className="text-lg font-bold text-foreground">{res.dataQuality.sourcesReferenced}</p>
                    </div>
                    <div className="rounded-lg border bg-emerald-500/5 p-3 text-center">
                      <p className="text-xs text-muted-foreground">Verified Claims</p>
                      <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400">{res.dataQuality.verifiedClaims}</p>
                    </div>
                    <div className="rounded-lg border bg-rose-500/5 p-3 text-center">
                      <p className="text-xs text-muted-foreground">Unverified</p>
                      <p className="text-lg font-bold text-rose-600 dark:text-rose-400">{res.dataQuality.unverifiedClaims}</p>
                    </div>
                    <div className="rounded-lg border bg-muted/30 p-3 text-center">
                      <p className="text-xs text-muted-foreground">Confidence</p>
                      <Progress value={res.confidenceScore} className="h-2 mt-2" />
                    </div>
                  </div>
                )}

                {/* Weak Signals */}
                {res.weakSignals?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                      <Eye className="h-4 w-4 text-violet-500" />
                      Weak Signals ({res.weakSignals.length})
                    </h4>
                    <div className="space-y-3">
                      {res.weakSignals.map((signal: any, i: number) => (
                        <div key={i} className="rounded-lg border p-3 hover:bg-muted/30 transition-colors">
                          <div className="flex items-start justify-between gap-3">
                            <p className="text-sm font-medium text-foreground flex-1">{signal.signal}</p>
                            <div className="flex items-center gap-1.5 shrink-0">
                              {getStrengthBadge(signal.strength)}
                              {getVerificationBadge(signal.verificationStatus)}
                            </div>
                          </div>
                          <div className="flex items-center gap-3 mt-2 flex-wrap">
                            <Badge variant={signal.direction === 'bullish' ? 'default' : signal.direction === 'bearish' ? 'destructive' : 'secondary'} className="text-xs">
                              {signal.direction}
                            </Badge>
                            <span className="text-xs text-muted-foreground">Source: {signal.source}</span>
                            <span className="text-xs text-muted-foreground">Horizon: {signal.timeHorizon}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Contrary Evidence */}
                {res.contraryEvidence?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-amber-500" />
                      Contrary Evidence
                    </h4>
                    <ul className="space-y-1.5">
                      {res.contraryEvidence.map((item: string, i: number) => (
                        <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                          <span className="text-amber-500 mt-0.5 shrink-0">⚠</span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Key Risks & Actionable Insights side by side */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {res.keyRisks?.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-foreground mb-2">Key Risks</h4>
                      <ul className="space-y-1.5">
                        {res.keyRisks.map((risk: string, i: number) => (
                          <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                            <span className="text-rose-500 mt-0.5 shrink-0">•</span>{risk}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {res.actionableInsights?.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-foreground mb-2">Actionable Insights</h4>
                      <ul className="space-y-1.5">
                        {res.actionableInsights.map((insight: string, i: number) => (
                          <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                            <span className="text-emerald-500 mt-0.5 shrink-0">✓</span>{insight}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {/* Disclaimers */}
                {res.dataQuality?.disclaimers?.length > 0 && (
                  <div className="rounded-lg bg-muted/50 p-3">
                    <p className="text-xs font-medium text-muted-foreground mb-1">Disclaimers</p>
                    {res.dataQuality.disclaimers.map((d: string, i: number) => (
                      <p key={i} className="text-xs text-muted-foreground">{d}</p>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })()}

        {/* Model Comparison Grid (when not expanding a single model) */}
        {!expandedModel && modelResults.filter(r => r.status === 'success').length > 1 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Model Comparison</CardTitle>
              <CardDescription>Click a model above to see detailed results</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-3 font-medium text-muted-foreground">Model</th>
                      <th className="text-center py-2 px-3 font-medium text-muted-foreground">Sentiment</th>
                      <th className="text-center py-2 px-3 font-medium text-muted-foreground">Confidence</th>
                      <th className="text-center py-2 px-3 font-medium text-muted-foreground">Signals</th>
                      <th className="text-center py-2 px-3 font-medium text-muted-foreground">Sources</th>
                      <th className="text-center py-2 px-3 font-medium text-muted-foreground">Verified</th>
                      <th className="text-center py-2 px-3 font-medium text-muted-foreground">Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {modelResults.filter(r => r.status === 'success').map(mr => {
                      const res = mr.result;
                      return (
                        <tr
                          key={mr.modelId}
                          className="border-b hover:bg-muted/30 cursor-pointer"
                          onClick={() => setExpandedModel(mr.modelId)}
                        >
                          <td className="py-2 px-3 font-medium">{mr.modelLabel}</td>
                          <td className="py-2 px-3 text-center">{getSentimentBadge(res?.overallSentiment)}</td>
                          <td className="py-2 px-3 text-center font-semibold">{res?.confidenceScore}%</td>
                          <td className="py-2 px-3 text-center">{res?.weakSignals?.length || 0}</td>
                          <td className="py-2 px-3 text-center">{res?.dataQuality?.sourcesReferenced || 0}</td>
                          <td className="py-2 px-3 text-center text-emerald-600 dark:text-emerald-400">{res?.dataQuality?.verifiedClaims || 0}</td>
                          <td className="py-2 px-3 text-center text-muted-foreground">{mr.elapsedMs ? `${(mr.elapsedMs / 1000).toFixed(1)}s` : '-'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Error results */}
        {modelResults.filter(r => r.status === 'error').length > 0 && !searching && (
          <Card className="border-rose-500/20">
            <CardHeader className="pb-2">
              <CardTitle className="text-base text-rose-600 dark:text-rose-400 flex items-center gap-2">
                <XCircle className="h-4 w-4" />
                Failed Models
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {modelResults.filter(r => r.status === 'error').map(mr => (
                  <div key={mr.modelId} className="flex items-center justify-between text-sm">
                    <span className="font-medium">{mr.modelLabel}</span>
                    <span className="text-xs text-rose-600 dark:text-rose-400">{mr.error}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Price Expectations Panel */}
        {modelResults.filter(r => r.status === 'success').length > 0 && (
          <PriceExpectationPanel topic={topic} modelResults={modelResults} />
        )}

        {/* Trend Comparison Panel */}
        {showTrendComparison && savedSearches.length >= 2 && (
          <WeakSentimentTrendComparison
            savedSearches={savedSearches}
            onClose={() => setShowTrendComparison(false)}
            onDrillToSearch={(search) => { loadSearch(search); setShowTrendComparison(false); }}
          />
        )}

        {/* History Panel */}
        {showHistory && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <History className="h-4 w-4 text-muted-foreground" />
                Saved Searches
              </CardTitle>
              <CardDescription>Click to reload a previous research session</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              {loadingHistory ? (
                <div className="p-6 space-y-3">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : savedSearches.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">
                  <Clock className="h-8 w-8 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No saved searches yet. Run your first search above.</p>
                </div>
              ) : (
                <div className="divide-y">
                  {savedSearches.map(search => (
                    <div key={search.id} className="flex items-center justify-between px-4 py-3 hover:bg-muted/30 transition-colors">
                      <button onClick={() => loadSearch(search)} className="flex-1 text-left">
                        <p className="text-sm font-medium text-foreground">{search.topic}</p>
                        <div className="flex items-center gap-3 mt-0.5">
                          <span className="text-xs text-muted-foreground">
                            {new Date(search.created_at).toLocaleDateString(undefined, {
                              weekday: 'short', year: 'numeric', month: 'short', day: 'numeric',
                              hour: '2-digit', minute: '2-digit'
                            })}
                          </span>
                          <Badge variant="secondary" className="text-xs">{search.models_used} models</Badge>
                        </div>
                      </button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-destructive"
                        onClick={() => deleteSearch(search.id)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Empty State */}
        {modelResults.length === 0 && !showHistory && (
          <Card className="border-dashed">
            <CardContent className="py-16 text-center">
              <Eye className="h-12 w-12 mx-auto mb-4 text-muted-foreground/30" />
              <p className="text-lg font-medium text-foreground mb-2">Discover Weak Sentiment Signals</p>
              <p className="text-sm text-muted-foreground max-w-md mx-auto">
                Enter a thematic research topic above to scan for early-stage sentiment shifts,
                contrarian indicators, and under-reported market signals using 6 AI models with strict verification guardrails.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Disclaimer */}
        <p className="text-xs text-muted-foreground text-center max-w-2xl mx-auto">
          Weak sentiment analysis is powered by multiple AI models with strict anti-hallucination guardrails.
          All results are cross-verified and tagged with verification status. This is for research purposes only
          and does not constitute financial advice.
        </p>
      </div>
    </div>
  );
};

export default WeakSentiments;
